import { GridApi, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { ApolloError } from '@parkchirp/api-client';
import { MutableRefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { LeasingRight } from '../types/leases';
import { isOwnerPortal, isTenantPortal } from './constants';
import { dayjs } from './dayjs';

export enum LeasingRightsSpaceTypeDisplay {
	RESERVED = 'Reserved',
	SHARED = 'Shared',
	UNRESERVED = 'Unreserved',
}

export enum LeasingRightsProductTypeDisplay {
	MONTHLY = 'Monthly',
	VALIDATION = 'Validation',
}

export const leasingConversionRightInitialRow = (startDate: string, endDate: string) => ({
	rateId: null,
	sublotId: null,
	leasingConversionRightHistory: [
		{
			startDate,
			endDate,
			daysRequiredForNoticeToConvert: null,
			conversionCount: 0,
		},
	],
	isNew: true,
});

export const leasingCountAndRateInitialRow = (commencementDate: string, expirationDate: string) => ({
	rateId: null,
	sublotId: null,
	isNew: true,
	leasingCountAndRateHistory: [
		{
			startDate: commencementDate,
			endDate: expirationDate,
			minCount: 0,
			maxCount: null,
			rightToConvert: false,
			mustTakeDollarAmount: null,
			enforceMaxCount: false,
		},
	],
});

export const requiredFieldsLeasingRight = {
	rightName: 'Right Name',
	commencementDate: 'Commencement Date',
	expirationDate: 'Expiration Date',
};

export const requiredFieldsLeasingCountAndRate = {
	rateId: 'Rate Name',
	sublotId: 'Sublot',
};

export const requiredFieldsConversionRightsHistory = {
	conversionCount: 'Conversion Count',
};

export const leasingRightFields = [
	'subtenantAccountIds',
	'suites',
	'squareFeet',
	'commencementDate',
	'expirationDate',
	'product',
	'mustTakeRateId',
	'multipleRateMustTakeDollarAmount',
	'extensionOptions',
	'rightName',
];

const numberFields = [
	'squareFeet',
	'multipleRateMustTakeDollarAmount',
	'mustTakeDollarAmount',
	'minCount',
	'maxCount',
	'sublotId',
];

const dateFields = ['commencementDate', 'expirationDate', 'startDate', 'endDate'];

export const currencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
});

export const getMissingFields = (formData, requiredFields, sublotsList?: any[]) =>
	Object.keys(requiredFields).reduce((acc, curr) => {
		if (curr === 'buildings' && !formData[curr]?.length) {
			return [...acc, requiredFields[curr]];
		}

		if (curr === 'sublotId' && !sublotsList?.find((lot) => lot.id === formData.sublotId)) {
			return [...acc, requiredFields[curr]];
		}

		const hasValue = !!formData[curr];

		if (!hasValue) {
			return [...acc, requiredFields[curr]];
		}
		return acc;
	}, []);

export const emptyStringToNull = (someObject) =>
	Object.entries(someObject).reduce(
		(acc, [key, val]) => ({
			...acc,
			[key]: val === '' || val === 'N/A' ? null : val,
		}),
		{},
	);

export const formatFormData = (name: string, value: any) => {
	if (['daysRequiredForNoticeToConvert', 'conversionCount'].includes(name)) {
		return value ? parseInt(value, 10) : null;
	}

	if (dateFields.includes(name)) {
		return value.format();
	}

	return value;
};

export const getSelectedRate = (rates, formData, rateKey = 'rateId') => {
	const rate = rates.find(({ id }) => id === formData[rateKey]);

	if (!rate) {
		return null;
	}

	return {
		rateId: rate.id,
		name: rate.name,
		shouldBeDisabled: !rate.active,
	};
};

export const formatLeasingRightsFormData = (name: string, value: any) => {
	if (name === 'rightToConvert' || name === 'enforceMaxCount') {
		return value === 'true';
	}

	if (dateFields.includes(name)) {
		return value.format();
	}

	// MUI number input does not work in FireFox so we'll need to manually convert these to numbers
	if (numberFields.includes(name)) {
		return value !== '' ? parseInt(value, 10) : null;
	}

	return value;
};

// Needed since MUI Select component needs referential equality between selected item and list of items
export const findSelectedBuildings = (allBuildings, selectedBuildings) =>
	allBuildings.reduce((acc, curr) => {
		if (selectedBuildings?.find((b) => b.id === curr.id)) {
			return [...acc, curr];
		}
		return acc;
	}, []);

export const validateConversionRightDates = (
	leasingRightStartDate: string,
	leasingRightEndDate: string,
	startDate: string,
) => {
	const leasingRightStart = dayjs.utc(leasingRightStartDate);
	const leasingRightEnd = dayjs.utc(leasingRightEndDate);
	const conversionRightStart = dayjs.utc(startDate);

	const dateRange = `${leasingRightStart.format('MM/DD/YYYY')} - ${leasingRightEnd.format('MM/DD/YYYY')}`;

	if (!conversionRightStart.isBetween(leasingRightStart, leasingRightEnd, 'day', '[]')) {
		return `Start date must be between leasing right start date and end date: ${dateRange}`;
	}

	return null;
};

export const validateLeasingRightDates = (leasingRightStartDate: string, leasingRightEndDate: string) => {
	if (dayjs.utc(leasingRightEndDate).isSameOrBefore(dayjs.utc(leasingRightStartDate))) {
		return `Expiration date cannot be on or before the commencement date`;
	}

	return null;
};

export const validateLeasingCounts = (formData) => {
	const { leasingCountAndRateHistory } = formData;

	if (leasingCountAndRateHistory.some(({ minCount, maxCount }) => !minCount && !maxCount)) {
		return `Please set either min or max count`;
	}

	if (leasingCountAndRateHistory.some(({ minCount, maxCount }) => maxCount && maxCount < (minCount ?? 0))) {
		return `Max count must be greater than or equal to min count`;
	}

	return null;
};

export const validateEnforcedMaxForSequencing = (histories: any[]) => {
	const enforcedMax = histories.some(
		(history) => dayjs(history.endDate).isSameOrAfter(dayjs()) && history.enforceMaxCount,
	);

	if (enforcedMax) {
		return null;
	}

	return 'Sequencing is enabled - must have at least one Count and Rate with an enforced max';
};

export const errorMessageMap = {
	INVALID_MAX_COUNT: "Max count can't be less than the total of all conversion right counts",
};

export const errorCodes = ['BAD_USER_INPUT'];

export const leasingRightErrorMessage = (err: ApolloError): string => {
	if (errorCodes.includes(err.graphQLErrors?.[0]?.extensions.code as string)) {
		return err.message;
	}

	if (!err.message) {
		return 'Internal Server Error';
	}

	if (errorMessageMap[err.message]) {
		return errorMessageMap[err.message];
	}

	console.log(err);
	return 'Internal Server Error';
};

export type DefaultEditModes = { [key in string | number]: boolean };

export function getDefaultEditModes(items: { id: number | string }[]) {
	return items.reduce(
		(acc, { id }) => ({
			...acc,
			[id]: false,
		}),
		{} as DefaultEditModes,
	);
}

type StopAllEditsInput = {
	saveChanges: boolean;
	gridApiRef: MutableRefObject<GridApi>;
	columns: GridColDef[];
};

export function stopAllEdits({ saveChanges, gridApiRef, columns }: StopAllEditsInput) {
	const allFields = columns.map(({ field }) => field);
	const allRowIds = gridApiRef.current.getAllRowIds();

	const cellsInEditMode = allRowIds.flatMap((id) =>
		allFields
			.filter((field) => gridApiRef.current.getCellMode(id, field) === 'edit')
			.map((field) => ({ id, field })),
	);

	cellsInEditMode.forEach(({ id, field }) => {
		gridApiRef.current.stopCellEditMode({ id, field, ignoreModifications: !saveChanges });
		gridApiRef.current.updateRows([gridApiRef.current.getRowWithUpdatedValues(id, field)]);
	});
}

export function getAllowRowEdits(
	row: GridRowParams['row'],
	uneditedHistory: { id: number | string; startDate: string }[],
	facilityTimezone: string,
	newMode: boolean,
	subscriptionHasInvoice: boolean,
) {
	if (newMode || !subscriptionHasInvoice) return true;

	const now = dayjs().tz(facilityTimezone);
	const startDateIsPast = dayjs(row.startDate).tz(facilityTimezone, true).isSameOrBefore(now, 'day');
	const startDateIsEdited = uneditedHistory.find((h) => h.id === row.id)?.startDate !== row.startDate;

	return startDateIsEdited || !startDateIsPast;
}

export function getGridRows(gridApiRef: MutableRefObject<GridApi>) {
	return [...(gridApiRef.current.getRowModels?.().values() ?? [])] as any[];
}

export function getTableLatestEndDate(gridApiRef: MutableRefObject<GridApi>) {
	const [latestEndDate] = getGridRows(gridApiRef)
		.map(({ endDate }) => dayjs(endDate))
		.sort((a, b) => (a.isAfter(b) ? -1 : 1));

	return latestEndDate;
}

export function getEmptyCountAndRate(leasingRight: { commencementDate: string; expirationDate: string }) {
	return {
		id: `new-${uuidv4()}`,
		rateId: '',
		rateName: '',
		sublotId: 0,
		sublotName: '',
		grossRatePrice: '',
		history: [
			{
				id: `new-${uuidv4()}`,
				startDate: leasingRight.commencementDate,
				endDate: leasingRight.expirationDate,
				minCount: 0,
				maxCount: null,
				enforceMaxCount: false,
				rightToConvert: false,
			},
		],
		conversionRights: [],
	};
}

export function validateLeaseRequiredFields(leasingRight: LeasingRight) {
	const requiredStringFields = [
		{ key: 'rightName', label: 'Right Name' },
		{ key: 'commencementDate', label: 'Commencement Date' },
		{ key: 'expirationDate', label: 'Expiration Date' },
	];

	const missingFields = [...requiredStringFields.filter(({ key }) => !leasingRight[key])];

	if (missingFields.length) {
		throw new Error(`Missing Required Fields: ${missingFields.map(({ label }) => label).join(', ')}`);
	}
}

export function getLeaseDetailPageUrl(leasingRightId: number): string {
	let baseUrl = '';

	if (isOwnerPortal) {
		baseUrl = `/lease-detail/${leasingRightId}`;
	} else if (isTenantPortal) {
		baseUrl = '/lease';
	} else {
		baseUrl = '/lease-detail/';
	}

	const url = new URL(baseUrl, window.location.href);

	if (!isOwnerPortal) {
		url.searchParams.set('id', String(leasingRightId));
	}
	return url.href;
}
